/**
 * English (`en`) string catalogue — the canonical reference for every
 * translatable UI string. Other locales (see `./cs.ts`) mirror the same
 * key shape. Keys are grouped by surface: `towers`, `abilities`, `hud`,
 * `hints`, `puzzle`, `profile`, `overlay`, etc.
 *
 * Keep this file the **authoritative key set**. The i18n loader validates
 * that every locale provides the same flat key path (see `i18n.spec.ts`).
 *
 * `{var}` placeholders are interpolated by {@link Catalogue} via the `t()`
 * helper in `./index.ts`. Strings that hold no placeholders are plain.
 *
 * The catalogue is UI-only — the simulation never reads it.
 */

/**
 * The structural type of a locale catalogue — derived from `en` but with
 * every leaf widened to `string` so a Czech mirror can supply translations
 * without TS treating them as mismatches.
 */
export interface Catalogue {
  meta: { name: string; code: string };
  towers: Record<TowerKey, { label: string; short: string; role: string }>;
  roleHints: Record<TowerKey, string>;
  hints: Record<'build' | 'placed' | 'wave1' | 'select' | 'brownout', string>;
  contextHints: Record<'overload' | 'profileKey' | 'endlessMod' | 'puzzleHover', string>;
  hud: Record<HudKey, string> & {
    /** Per-multiplier label inside the Overload-dial readout (Sprint 69). */
    tierMult: Record<'hp' | 'sp' | 'cn' | 'gd' | 'sc', string>;
    /** Compact enemy names for the wave-preview row (Sprint 69). */
    spawnShort: Record<SpawnTypeKey, string>;
  };
  modes: Record<ModeKey, string>;
  modeSub: Record<'campaign' | 'endless' | 'maze' | 'puzzle', string>;
  modeBlurb: Record<ModeKey, string>;
  /**
   * Sprint 70 (mode-clarity-impl-s70) — per-mode hover info-card copy. The
   * card appears under the chip the cursor is over (not the active one)
   * and lists, in order: a short tagline, three bullets of "what makes
   * this mode different", a session length hint and a "skill exercised"
   * line. See `docs/MODE_CLARITY_DESIGN.md` §F3 for the card layout, copy
   * budget and intent.
   */
  modeCard: Record<
    ModeKey,
    { tagline: string; bullet1: string; bullet2: string; bullet3: string; length: string; skill: string }
  >;
  difficulty: Record<DifficultyKey, string>;
  difficultySub: Record<DifficultyKey, string>;
  overlay: Record<OverlayKey, string>;
  profile: Record<ProfileKey, string> & {
    /** Sprint 69 — unlock-tree hover preview (one line per node id). */
    nodePreview: Record<NodePreviewKey, string>;
  };
  help: Record<HelpKey, string>;
  intro: Record<IntroKey, string>;
  puzzle: {
    label: string;
    levels: Record<string, { name: string; hint: string }>;
    /**
     * Sprint 63 (puzzle-ux-briefing-s63) — briefing-card + Win / Lose
     * overlay UI strings (`puzzle.briefing.*`). Optional on the catalogue
     * shape so this sprint can ship in EN + CS without breaking the ten
     * other locales whose catalogues live in `i18n-10langs-s58`'s parallel
     * sprint; the runtime `t()` helper falls back to EN for any locale
     * missing the keys. When `i18n-10langs-s58` extends the catalogue,
     * it can move these to required without touching this module.
     */
    briefing?: Record<PuzzleBriefingKey, string>;
    /** Sprint 69 — puzzle level-select overlay chrome. */
    select?: Record<PuzzleSelectKey, string>;
  };
  localeChip: { label: string; en: string; cs: string };
  resume: Record<'title' | 'resume' | 'newRun' | 'hint' | 'detail', string>;
  /**
   * Sprint 69 — Kernel boss UI chrome (HP-bar header, phase tag, the
   * pre-spawn banner, the Power-Drain warning). Rerouted out of
   * render.ts literals through `t()`.
   */
  boss: Record<'kernelHeader' | 'phase' | 'powerDrain' | 'kernelIncoming', string> & {
    phaseName: Record<'boot' | 'fork' | 'panic', string>;
  };
  /** Sprint 69 — CONFIG.maps[*].label / tagline route. */
  maps: Record<MapIdKey, { label: string; tagline: string }>;
  /** Sprint 69 — CONFIG.abilities[*].name route. */
  abilities: Record<AbilityIdKey, { label: string }>;
  /** Sprint 69 — CONFIG.overloadTiers[*].name route. */
  overload: { tierName: Record<OverloadTierKey, string> };
  /** Sprint 69 — ENDLESS_MODIFIERS[*].label route. */
  endlessMod: Record<EndlessModKey, { label: string }>;
  /** Sprint 69 — CONFIG.enemies[*].label route (used by Help roster). */
  enemies: Record<SpawnTypeKey, { label: string }>;
  /** Sprint 69 — Help-panel one-liner gimmick per enemy. */
  enemyRole: Record<SpawnTypeKey, string>;
  /** Sprint 69 — Help-panel one-liner role per ability. */
  abilityRole: Record<AbilityIdKey, string>;
  /** Sprint 69 — tooltip chrome (cost / level / cooldown / stat lines). */
  tooltip: {
    levelOf: string;
    credits: string;
    next: string;
    cooldown: string;
    stat: Record<
      | 'supply'
      | 'dmgRngCd'
      | 'pwrChain'
      | 'pwrAoe'
      | 'slow'
      | 'brittle'
      | 'pwrBlast'
      | 'pwrSingle',
      string
    >;
  };
  /** Sprint 69 — Help-panel keyboard-control row labels. */
  controls: Record<
    'arm' | 'mouse' | 'space' | 'fast' | 'upgradeSell' | 'target' | 'profile' | 'utility',
    string
  >;
  /** Sprint 69 — unlock-node display names (mirrors CONFIG.unlocks[*].name). */
  unlocks: Record<UnlockIdKey, { name: string }>;
  /**
   * Sprint 73 (tutorial-impl-s73) — interactive tour copy: title-screen
   * button labels, the 12 step bodies + titles, and the overlay chrome
   * (eyebrow / next / back / skip / play / skip-confirm). See
   * `docs/TUTORIAL_DESIGN.md` §6 for the canonical key set and English
   * source-of-truth copy. Optional on the catalogue shape (mirroring the
   * Sprint 62 / 63 forward-compat pattern) — a locale that doesn't ship
   * the `tutorial.*` block falls back to EN via the runtime `t()` helper
   * rather than breaking parity. All 12 shipped locales DO carry the keys
   * in this sprint (i18n.spec parity covers them).
   */
  tutorial?: {
    button: Record<'start' | 'startSub' | 'replay' | 'replaySub', string>;
    step: Record<TutorialStepIdKey, { title: string; body: string }>;
    ui: Record<'eyebrow' | 'next' | 'back' | 'skip' | 'play' | 'skipConfirm', string>;
  };
  /**
   * Sprint 62 (auth-frontend-impl-s62) — start-screen sign-in modal copy
   * (see `docs/AUTH_DESIGN.md` §3). Marked **optional** on the catalogue
   * shape so this sprint can ship EN + CS without breaking the ten other
   * locales whose catalogues live in `i18n-10langs-s58`'s parallel
   * sprint; the runtime `t()` helper falls back to EN for any locale
   * missing the keys, which is the desired progressive-localisation path.
   * When `i18n-10langs-s58` extends the catalogue, it can move these
   * to required without touching this module.
   */
  auth?: Record<AuthKey, string>;
  /**
   * Sprint 62 — cookies-banner copy (see `docs/AUTH_DESIGN.md` §5).
   * Same optional rationale as `auth` above: ships in EN + CS only this
   * sprint; other locales fall back to EN via the runtime `t()` helper.
   */
  cookies?: Record<CookiesKey, string>;
}

/**
 * Sprint 62 — start-screen sign-in modal keys (`auth.*`). See
 * `docs/AUTH_DESIGN.md` §3.2 for the modal layout and §3.4 for the
 * first-load detection that gates it.
 */
/**
 * Sprint 73 (tutorial-impl-s73) — the 12 step ids of the opt-in interactive
 * tour (see `docs/TUTORIAL_DESIGN.md` §3). Exported so the `Tutorial` state
 * machine can index its step defs against the same union the catalogue
 * keys on, without redefining the list in two places.
 */
export type TutorialStepIdKey =
  | 'welcome'
  | 'path'
  | 'firstPlace'
  | 'wave1'
  | 'towerRoles'
  | 'powerGrid'
  | 'overloadDial'
  | 'overclock'
  | 'abilities'
  | 'mazePreview'
  | 'puzzlePreview'
  | 'wrapUp';

type AuthKey =
  | 'title' | 'subtitle' | 'signInDiscord' | 'signInGoogle' | 'signInEmail'
  | 'playAsGuest' | 'comingSoon' | 'guestMode'
  | 'signOut' | 'signOutBody'
  | 'orDivider';

/**
 * Sprint 62 — cookies-banner keys (`cookies.*`). See
 * `docs/AUTH_DESIGN.md` §5 for the copy + the "stylish, not GDPR-fluff"
 * intent. The four required keys are title / body / accept / essentials;
 * the i18n test enforces shape parity, so any future addition lands here.
 */
type CookiesKey = 'title' | 'body' | 'accept' | 'essentials';

/**
 * Sprint 63 (puzzle-ux-briefing-s63) — briefing-card + Win / Lose overlay
 * UI strings. Per-level briefing copy lives in `src/game/puzzles.ts`
 * (`PuzzleLevel.briefing`), not in i18n; only the **chrome** is
 * translated (button labels, section headings, status pills). Keeping the
 * per-level "aha" + win condition out of i18n simplifies authoring — a
 * new level lands as a single record in `puzzles.ts` and the 12-locale
 * agent has nothing to mirror.
 */
type PuzzleBriefingKey =
  | 'eyebrow' | 'startBtn' | 'cancelBtn'
  | 'sectionConstraints' | 'sectionTowers' | 'sectionWinCondition'
  | 'budgetLabel' | 'wavesLabel'
  | 'towerAllowed' | 'towerForbidden'
  | 'resultEyebrowWon' | 'resultEyebrowLost'
  | 'resultTitleWon' | 'resultTitleLost'
  | 'resultStatTowers' | 'resultStatGold' | 'resultStatLives'
  | 'resultNextBtn' | 'resultReplayBtn' | 'resultResetBtn' | 'resultBackBtn';

/**
 * Sprint 69 (i18n-gaps-extract-s69) — puzzle level-select overlay UI strings
 * (`puzzle.select.*`). Optional on the catalogue shape for the same
 * forward-compat reasons as `puzzle.briefing` above — the runtime `t()`
 * helper falls back to EN for any locale missing the keys.
 */
type PuzzleSelectKey =
  | 'subtitle' | 'close'
  | 'cardTitle' | 'lockedCard'
  | 'solved' | 'unsolved' | 'noSolve'
  | 'bestLine' | 'allowed'
  | 'parPlaceholder' | 'parRow';

type TowerKey = 'pulse' | 'arc' | 'cryo' | 'mortar' | 'sniper' | 'pylon' | 'wall';
type ModeKey = 'campaign' | 'endless' | 'maze' | 'puzzle';
type DifficultyKey = 'easy' | 'normal' | 'hard';
type HudKey =
  | 'buildZone' | 'waveZone' | 'abilitiesZone' | 'overloadZone'
  | 'startWave' | 'nextWave' | 'pause' | 'resume' | 'restart'
  | 'deployHint' | 'holdHint' | 'rebootHint' | 'nextWaveHint'
  | 'audioOn' | 'audioOff'
  | 'credits' | 'core' | 'wave' | 'endless' | 'score' | 'powerGrid'
  | 'runMode' | 'difficulty' | 'profile' | 'map' | 'clickHint'
  | 'locked' | 'lockedShort' | 'lockedTooltip' | 'lockedTowerNotice'
  | 'lockedAbility' | 'lockedOverload' | 'lockedMode' | 'lockedDifficulty'
  | 'cheatPip' | 'profilePip'
  | 'overloadIdle' | 'overloadTier' | 'overloadActive'
  | 'maxLevel' | 'upgrade' | 'sell' | 'target' | 'cycleTarget'
  | 'sellHint' | 'upgradeHint' | 'overclock' | 'overclockOn'
  // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
  | 'buildHintIntro' | 'buildHintReinforce'
  | 'mazeRoute' | 'tiles'
  | 'abilityActive' | 'costSuffix' | 'overclockPip' | 'powerReadout'
  | 'towerCostDraw' | 'pylonCostSupply' | 'wallCost'
  | 'endlessModBadge'
  | 'startWaveOverload' | 'waveOverloadPip'
  | 'overclockNoticeOn' | 'overclockNoticeOff';
type OverlayKey =
  | 'paused' | 'pauseHint'
  | 'missionComplete' | 'gridSecured' | 'wavesRepelled' | 'continueEndless'
  | 'unlockEndless' | 'restartCampaign'
  | 'systemFailure' | 'coreBreached' | 'endlessEnded' | 'reactorOverrun' | 'runItBack'
  | 'finalScore' | 'newHighScore' | 'newEndlessBest' | 'highScore' | 'endlessBest'
  // Sprint 69 — extracted from render.ts.
  | 'aftershock' | 'overcharge' | 'brownout';

// Sprint 69 — id unions for the CONFIG-driven groups added this sprint.
type SpawnTypeKey =
  | 'bit' | 'packet' | 'daemon' | 'fork' | 'cipher' | 'patch' | 'buffer' | 'streak' | 'kernel';
type MapIdKey = 'default' | 'cramped' | 'lines' | 'serpentine';
type AbilityIdKey = 'overcharge' | 'airstrike' | 'empBurst';
type OverloadTierKey = 'nominal' | 'charged' | 'surging' | 'critical' | 'meltdown';
type EndlessModKey =
  | 'armoredSurge' | 'fastPack' | 'scatteredSpread' | 'streakRunners' | 'daemonRush';
/**
 * Sprint 69 — unlock-id key suffix. The `CONFIG.unlocks[*].id` is dotted
 * (`tower.mortar`) which would collide with the `t()` dotted-path resolver
 * — so we use camel-case sub-keys here and the renderer maps the runtime id
 * (`unlockKeyForId` in `profileScreen.ts` / `hud.ts`).
 */
type UnlockIdKey =
  | 'towerMortar' | 'towerSniper' | 'abilityAirstrike' | 'abilityEmp'
  | 'modeEndless' | 'modeMaze' | 'difficultyHard'
  | 'overloadCritical' | 'overloadMeltdown'
  | 'themeAmber' | 'themePhosphor';
type NodePreviewKey = UnlockIdKey;
type ProfileKey =
  | 'title' | 'subtitle' | 'closeHint' | 'tabUnlocks' | 'tabScoreboard'
  | 'flux' | 'unlockTree' | 'unlockedCount' | 'lifetime'
  | 'branchArsenal' | 'branchModes' | 'branchChallenge' | 'branchCosmetic'
  | 'statRunsPlayed' | 'statCampaignWins' | 'statBestScore'
  | 'statDeepestEndless' | 'statTotalFlux'
  | 'personalBests' | 'waveLabel'
  | 'runHistory' | 'noRuns' | 'earlierRuns' | 'earlierRunsPlural'
  | 'colRun' | 'colMode' | 'colResult' | 'colOverload'
  | 'colScore' | 'colDeltaBest' | 'colFlux' | 'starBest'
  | 'statusUnlocked' | 'statusOwned' | 'statusRequiresPrereq'
  | 'statusFluxShort' | 'statusAffordable' | 'statusCost'
  | 'buyAction' | 'nodeReq'
  | 'profileLauncher' | 'profileKeyHint' | 'viewScoreboard'
  | 'newPersonalBest' | 'fromYourBest'
  | 'grantTower' | 'grantAbility' | 'grantMode' | 'grantDifficulty'
  | 'grantOverload' | 'grantTheme';
type HelpKey =
  | 'eyebrow' | 'title' | 'controls' | 'powerGrid' | 'powerGridBody'
  | 'objective' | 'objectiveBody' | 'mazeMode' | 'mazeModeBody'
  | 'glitches' | 'towers' | 'abilities' | 'replayIntro' | 'closeHint';
type IntroKey =
  | 'eyebrow' | 'titleBig' | 'subtitle'
  | 'line1' | 'line2' | 'line3' | 'line4'
  | 'gotIt' | 'gotItHint';

export const en: Catalogue = {
  meta: {
    name: 'English',
    code: 'EN',
  },

  // -------- Towers ----------------------------------------------------------
  // Labels mirror `CONFIG.towers[type].label`; `role` lines mirror the
  // Sprint-18 hover-tooltip role copy in `onboarding.ts`. `short` is the
  // BUILD-zone chip caption (limited to ~6 chars).
  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULSE',
      role: 'Neutral generalist. Reliable damage anywhere on the path.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARC',
      role: 'Power-grid coupled. Stronger with a Pylon surplus, weaker in a brownout.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Chokepoint painter. Small radius — slow + brittle-mark every enemy at one pinch.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MORTAR',
      role: 'Indirect fire. Place it BACK from the path — close-in is its dead zone.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Line-of-fire pierce. Strong on long straight runs; weak in corners.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: "Power supply. Build enough to cover your towers' demand (and an Arc-friendly surplus).",
    },
    wall: {
      label: 'Wall Node',
      short: 'WALL',
      role: 'Cheap blocker — Maze mode only. Shape the route the enemies must walk.',
    },
  },

  // -------- Role hints (one-time first-placement tour, Sprint 25) -----------
  roleHints: {
    pulse: 'Pulse Turret: your reliable default — solid damage anywhere on the path.',
    arc: 'Arc Coil: scales with your power surplus. Over-build Pylons for a stronger chain.',
    cryo: 'Cryo Emitter: tight chokepoint paint. Slow + brittle-mark the few tiles at a pinch.',
    mortar: "Splash Mortar: indirect fire. Place it BACK from the path — it can't hit close-in.",
    sniper: 'Sniper Rail: line-of-fire pierce. Align with a long straight for max effect.',
    pylon: "Power Pylon: feeds the grid. Build enough supply to cover your towers' demand.",
    wall: 'Wall: cheap blocker — shape the route the enemies must walk.',
  },

  // -------- Wave-1 contextual hints (Sprint 12 / 25) ------------------------
  hints: {
    build: 'Build a tower: pick one below (or press 1), then click a glowing tile near the path.',
    placed: 'Good. Add another, then press Space to send in the wave.',
    wave1: 'Towers fire automatically. Keep the power bar out of the red.',
    select: 'Click a placed tower to select it — press U to upgrade, S to sell.',
    brownout: 'Brownout! Towers throttled. Build a Pylon (press 3) to add power.',
  },

  // -------- Sprint 53 context hints -----------------------------------------
  contextHints: {
    overload:
      'Overload: dial up the next wave for bigger rewards — at higher risk. Slow start? Crank it for faster Flux.',
    profileKey: 'Press P to open your Profile — Flux unlocks new towers, abilities, and modes.',
    endlessMod: 'Endless rotates wave MODIFIERS — adapt your build.',
    puzzleHover:
      'Puzzle mode: hand-crafted logic levels with line-of-sight walls. Different game entirely.',
  },

  // -------- HUD chrome ------------------------------------------------------
  hud: {
    // Zone labels (COMMAND DOCK frame captions)
    buildZone: 'BUILD',
    waveZone: 'WAVE',
    abilitiesZone: 'ABILITIES',
    overloadZone: 'OVERLOAD',

    // WAVE-zone primary action button
    startWave: 'START WAVE',
    nextWave: 'NEXT WAVE',
    pause: 'PAUSE',
    resume: 'RESUME',
    restart: 'RESTART',
    deployHint: 'deploy [SPACE]',
    holdHint: 'hold [SPACE]',
    rebootHint: 'reboot [R]',
    nextWaveHint: 'NEXT WAVE',

    // Secondary toggles
    audioOn: 'AUDIO ON',
    audioOff: 'AUDIO OFF',

    // Stat chip labels
    credits: 'CREDITS',
    core: 'CORE',
    wave: 'WAVE',
    endless: 'ENDLESS',
    score: 'SCORE',
    powerGrid: 'POWER GRID',

    // Title-screen modules
    runMode: 'RUN MODE',
    difficulty: 'DIFFICULTY',
    profile: 'PROFILE',
    map: 'MAP',
    clickHint: '▷ click',

    // Locked badge
    locked: 'LOCKED',
    lockedShort: 'LOCKED',
    lockedTooltip:
      'LOCKED — unlock for ✦{cost} Flux in the Profile screen (press P).',
    lockedTowerNotice: '{tower} locked',
    lockedAbility: 'Ability locked',
    lockedOverload: 'Overload tier locked',
    lockedMode: '{mode} mode locked',
    lockedDifficulty: 'Hard difficulty locked',

    // Cheat / profile pips
    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    // Overload dial details
    overloadIdle: '— overload sets from wave 1 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ ACTIVE {sec}s',

    // Selection panel rows
    maxLevel: 'MAX LEVEL',
    upgrade: 'UPGRADE L{level}',
    sell: 'SELL',
    target: 'TARGET: {mode}',
    cycleTarget: 'cycle · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ ON',

    // Sprint 69 (i18n-gaps-extract-s69) — render.ts build-phase hint pill
    // (docs/I18N_GAPS.md Tier 1), the user-reported `?lang=cs` gap that
    // scoped the whole sprint.
    buildHintIntro:
      'BUILD PHASE  //  arm a tower [1·2·3·4·5], place it, then START WAVE [SPACE]',
    buildHintReinforce: 'BUILD PHASE  //  reinforce the grid, then START WAVE {wave} [SPACE]',

    // Maze-route badge (render.ts, top-right of the playfield in Maze mode).
    mazeRoute: 'M A Z E   R O U T E',
    tiles: 'TILES',

    // Wave-phase ability-slot status line / cost suffix.
    abilityActive: '◉ ACTIVE {sec}s',
    costSuffix: '{cost}c',
    // Power-bar OC ×N pip (hud.ts:1161).
    overclockPip: 'OC ×{n}',
    // Power-bar numeric readout (hud.ts:1104).
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    // BUILD-zone tower chip sub-lines.
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    // Endless modifier badge — the WAVE-zone "MOD · …" preview row.
    endlessModBadge: 'MOD · {label}',
    // Start-button sub-line during build phase when Overload > 0.
    startWaveOverload: '⚡{n} {tierName}',
    // Wave-phase ABILITIES-zone caption when a locked-in Overload runs.
    waveOverloadPip: '⚡{n} {tierName}',
    // Game.ts:1198 — Overclock-toggle confirmation notice ("{tower} Overclock ON" / "OFF").
    overclockNoticeOn: '{tower} Overclock ON',
    overclockNoticeOff: '{tower} Overclock OFF',

    // ----- nested sub-records (Sprint 69) ----------------------------------
    /** Overload-dial multiplier labels (hud.ts:1533–1537). */
    tierMult: {
      hp: 'HP',
      sp: 'SP',
      cn: 'CN',
      gd: 'GD',
      sc: 'SC',
    },
    /** Compact enemy names for the wave-preview row (hud.ts SPAWN_SHORT). */
    spawnShort: {
      bit: 'Bit',
      packet: 'Pkt',
      daemon: 'Dmn',
      fork: 'Fork',
      cipher: 'Cph',
      patch: 'Pch',
      buffer: 'Buf',
      streak: 'Stk',
      kernel: 'KERNEL',
    },
  },

  // -------- Run modes & difficulty chips ------------------------------------
  modes: {
    campaign: 'CAMPAIGN',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 waves · win',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'pick a level',
  },
  modeBlurb: {
    campaign: '10 waves. Boss at wave 10. Win and you graduate.',
    endless: 'Same map. Modifiers every wave. Adapt or leak.',
    maze: 'Open field. You shape the path with walls. Slow them down or starve their reward.',
    puzzle: '10 hand-authored levels. Find the one winning layout.',
  },
  modeCard: {
    campaign: {
      tagline: 'Learn the grid, beat the boss.',
      bullet1: '10 hand-tuned waves + boss',
      bullet2: 'Fixed S-path — build, wave, upgrade',
      bullet3: 'Win unlocks Endless on this map',
      length: '~8 min',
      skill: 'Exercises build order',
    },
    endless: {
      tagline: 'Adapt or leak. There is no end.',
      bullet1: 'Plays Campaign 1–10, then scales forever',
      bullet2: 'New wave modifier every wave',
      bullet3: 'Run ends when the Core breaches',
      length: '~30 min',
      skill: 'Exercises long-term pivots',
    },
    maze: {
      tagline: 'You build the maze enemies must walk.',
      bullet1: 'Open field — no pre-drawn path',
      bullet2: 'Towers + cheap walls shape the route',
      bullet3: 'Longer route = more time in your fire',
      length: '~12 min',
      skill: 'Exercises spatial routing',
    },
    puzzle: {
      tagline: 'One layout wins. Find it.',
      bullet1: '10 authored levels, single solution',
      bullet2: 'Fixed budget + fixed wave',
      bullet3: 'Line-of-sight walls + tower limits',
      length: '~2 min each',
      skill: 'Exercises deduction',
    },
  },
  difficulty: {
    easy: 'EASY',
    normal: 'NORMAL',
    hard: 'HARD',
  },
  difficultySub: {
    easy: 'gentle',
    normal: 'balanced',
    hard: '✦ {cost}',
  },

  // -------- Win / Lose / Pause overlays -------------------------------------
  overlay: {
    paused: 'PAUSED',
    pauseHint: 'PRESS  SPACE  TO  RESUME',

    missionComplete: '// MISSION COMPLETE',
    gridSecured: 'GRID SECURED',
    wavesRepelled: 'All {total} assault waves repelled.',
    continueEndless: 'CONTINUE  →  ENDLESS',
    unlockEndless: 'unlock Endless in the Profile to continue',
    restartCampaign: 'or press  R  to restart campaign',

    systemFailure: '// SYSTEM FAILURE',
    coreBreached: 'CORE BREACHED',
    endlessEnded: 'Endless run ended at wave {wave}.',
    reactorOverrun: 'Reactor overrun on wave {wave} of {total}.',
    runItBack: 'PRESS  R  TO  RUN  IT  BACK',

    finalScore: 'FINAL SCORE',
    newHighScore: '★  NEW HIGH SCORE  ★',
    newEndlessBest: '★  NEW ENDLESS BEST  ★',
    highScore: 'HIGH SCORE',
    endlessBest: 'ENDLESS BEST',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: '// AFTERSHOCK — GRID PAYBACK  {sec}s //',
    overcharge: '// OVERCHARGE — GRID BOOSTED  {sec}s //',
    brownout: '// GRID BROWNOUT — TOWERS THROTTLED //',
  },

  // -------- Profile / Scoreboard screen -------------------------------------
  profile: {
    title: 'PROFILE',
    subtitle: '// META-PROGRESSION',
    closeHint: 'press  P  or  ESC  to close',
    tabUnlocks: 'UNLOCKS',
    tabScoreboard: 'SCOREBOARD',
    flux: 'FLUX',
    unlockTree: 'UNLOCK TREE',
    unlockedCount: '{owned} / {total} UNLOCKED',
    lifetime: 'LIFETIME',

    // Branch headers
    branchArsenal: 'ARSENAL',
    branchModes: 'MODES',
    branchChallenge: 'CHALLENGE',
    branchCosmetic: 'COSMETIC',

    // Stat cells
    statRunsPlayed: 'RUNS PLAYED',
    statCampaignWins: 'CAMPAIGN WINS',
    statBestScore: 'BEST SCORE',
    statDeepestEndless: 'DEEPEST ENDLESS',
    statTotalFlux: 'TOTAL FLUX',

    // Personal-best grid
    personalBests: 'PERSONAL BESTS',
    waveLabel: 'WAVE {wave}',

    // Scoreboard run history
    runHistory: 'RUN HISTORY',
    noRuns: 'No runs recorded yet — finish a run to start the climb.',
    earlierRuns: '+ {n} earlier run',
    earlierRunsPlural: '+ {n} earlier runs',
    colRun: 'RUN',
    colMode: 'MODE · DIFFICULTY',
    colResult: 'RESULT',
    colOverload: 'OVL',
    colScore: 'SCORE',
    colDeltaBest: 'Δ BEST',
    colFlux: 'FLUX',
    starBest: '★ BEST',

    // Unlock-node status
    statusUnlocked: 'UNLOCKED',
    statusOwned: 'OWNED',
    statusRequiresPrereq: 'Requires {prereq} first',
    statusFluxShort: 'Need ✦{short} more Flux',
    statusAffordable: 'Affordable — click to unlock',
    statusCost: '✦{cost} to unlock',
    buyAction: 'BUY  ▸',
    nodeReq: 'REQ: {prereq}',

    // Launcher
    profileLauncher: 'PROFILE',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'VIEW  SCOREBOARD',
    newPersonalBest: '★  NEW PERSONAL BEST  ★',
    fromYourBest: 'FROM YOUR BEST',

    // Grant tags
    grantTower: 'WEAPON TOWER',
    grantAbility: 'ACTIVE ABILITY',
    grantMode: 'RUN MODE',
    grantDifficulty: 'DIFFICULTY',
    grantOverload: 'OVERLOAD TIER {tier}',
    grantTheme: 'BOARD THEME',

    // Sprint 69 — unlock-tree hover preview (was profileScreen.ts:NODE_PREVIEW
    // hardcoded English map). Keyed by `CONFIG.unlocks[*].id` so the screen
    // can resolve a node's preview live through `t('profile.nodePreview.' +
    // node.id)` without a per-locale shape change when a future unlock lands.
    nodePreview: {
      towerMortar:
        'Indirect-fire weapon. Place it BACK from the path — close-in is its dead zone; arcs shells onto clusters with armour-shred.',
      towerSniper:
        'Line-of-fire pierce lance. Aim down a long straight run — every enemy on the line takes the hit; weak in corners.',
      abilityAirstrike:
        'One-shot blast on a target tile (45c). Panic-clear a cluster or chip a tough enemy.',
      abilityEmp:
        'Field-wide stun (80c). Freeze every enemy for a moment when the lane is overrun.',
      modeEndless:
        'Play past wave 10 — procedural waves keep coming until the Core falls. New score-chase track.',
      modeMaze:
        'Open-field route-building mode. Towers block their tile and enemies A*-path around them.',
      difficultyHard:
        'Tougher waves; bigger gold and score multipliers. Unlocks the upper Flux band.',
      overloadCritical:
        'Overload tier 3 — turn up the heat for fatter score / Flux multipliers next wave.',
      overloadMeltdown:
        'Overload tier 4 — the hardest pre-set wave. Top score / Flux multipliers; only for a solid build.',
      themeAmber:
        'Cosmetic palette — warm amber CRT terminal. Pure cosmetic; no gameplay effect.',
      themePhosphor:
        'Cosmetic palette — vintage green phosphor. Pure cosmetic; no gameplay effect.',
    },
  },

  // -------- Help panel ------------------------------------------------------
  help: {
    eyebrow: '/ /  R E F E R E N C E',
    title: 'OPERATIONS MANUAL',
    controls: 'CONTROLS',
    powerGrid: 'THE POWER GRID',
    powerGridBody:
      'Weapons draw power; Pylons supply it. When demand outruns supply the grid browns out and every tower slows — build Pylons to keep firing.',
    objective: 'OBJECTIVE',
    objectiveBody:
      'Clear all 15 Campaign waves to win; lose the moment the Core’s lives hit zero. Endless mode runs on forever.',
    mazeMode: 'MAZE MODE',
    mazeModeBody:
      'A third run mode on an open field: towers block the tiles they sit on and enemies A*-path around them, so you build the maze. You cannot wall the Core off.',
    glitches: 'GLITCHES',
    towers: 'TOWERS',
    abilities: 'ABILITIES',
    replayIntro: '↺  REPLAY INTRO',
    closeHint: 'Esc / H / ? or click anywhere to close',
  },

  // -------- Intro overlay ---------------------------------------------------
  intro: {
    eyebrow: '/ /  S Y S T E M   B R I E F I N G',
    titleBig: 'OVERVOLT',
    subtitle: 'Defend the Core',
    line1:
      'Corrupted programs march down the circuit toward your Core. Build towers to destroy them before they break through.',
    line2:
      '⚡ Every tower draws power. Overbuild and the grid browns out — all towers slow at once. Build Pylons to keep the power on.',
    line3: 'Build in the calm phase, then press Start Wave. Survive 15 waves to win.',
    line4: 'Mouse to build · 1–5 select · Space start/pause · U upgrade · S sell',
    gotIt: 'GOT IT  →',
    gotItHint: 'press any key to continue',
  },

  // -------- Puzzle levels ---------------------------------------------------
  // `name` mirrors the authoring file (src/game/puzzles.ts) so the synthetic
  // map registry stays consistent (the registry takes the literal name).
  // The Czech mirror translates only the *display* name shown in the
  // puzzleSelect overlay; the underlying id never changes.
  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'First Light',
        hint: 'Place a Pulse next to the corridor. Any tile in range works.',
      },
      'p1-long-lance': {
        name: 'Long Lance',
        hint: 'One Sniper aligned with the lane pierces every enemy.',
      },
      'p2-over-the-wall': {
        name: 'Over the Wall',
        hint: 'The wall blocks every direct beam. Mortar shells arc over.',
      },
      'p3-funhouse-mirror': {
        name: 'Funhouse Mirror',
        hint: 'A Sniper threading the gap hits both lanes.',
      },
      'p4-surplus-riches': {
        name: 'Surplus Riches',
        hint: 'Over-build Pylons past the freeband. The Arc Coil thrives on surplus.',
      },
      'p5-pinch-point': {
        name: 'Pinch Point',
        hint: 'Cryo paints the choke; Pulses around it finish the bunch.',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher Gauntlet',
        hint: 'Mortar shreds armour; Cryo paints the bend; Sniper pierces the bottom leg. Use the whole kit.',
      },
      'p7-kernel-echo': {
        name: 'Kernel Echo',
        hint: 'Mortars lob over the LOS flanks. Sniper threads from below. Watch the Power Drain.',
      },
      'p8-overclock-crunch': {
        name: 'Overclock Crunch',
        hint: 'One Pulse cannot keep up. Overclock the burst window — sustained rate is not enough.',
      },
      'p9-pylon-pinch': {
        name: 'Pylon Pinch',
        hint: 'Cluster Pylons between your towers — the aura cap rewards a tight build zone.',
      },
      // Sprint 74b — hard tier (puzzle-rework-s74b).
      'p10-slit-lance': {
        name: 'Slit Lance',
        hint: 'One slit tile. Pick the right tower — there is no second placement.',
      },
      'p11-fortress': {
        name: 'Fortress',
        hint: 'Two Mortars, two tiles. The L bends past one annulus.',
      },
      'p12-brittle-math': {
        name: 'Brittle Math',
        hint: 'Pulse damage rounds against Cipher armor. Cryo brittle is the multiplier that closes the math.',
      },
      'p14-painters-cell': {
        name: "Painter's Cell",
        hint: 'Budget locks Pulse at L1. Cryo brittle is the multiplier that closes the math.',
      },
      'p15-two-mirrors': {
        name: 'Two Mirrors',
        hint: 'One pivot tile threads the spine gap. The lance hits both legs from there.',
      },
      'p16-mortar-annulus': {
        name: 'Mortar Annulus',
        hint: 'The U is wider than the donut. Two Mortars — one per leg.',
      },
      'p17-no-cryo-streaks': {
        name: 'No-Cryo Streaks',
        hint: 'Budget locks both at L1. Mortar rear, Sniper front — no shortcuts.',
      },
    },
    // Sprint 63 (puzzle-ux-briefing-s63) — briefing card + Win / Lose
    // overlay UI strings. Per-level `oneLine` / `winCondition` author copy
    // lives in `src/game/puzzles.ts:PuzzleLevel.briefing`, not here.
    briefing: {
      eyebrow: 'PUZZLE BRIEFING',
      startBtn: 'Start ▷',
      cancelBtn: 'Cancel',
      sectionConstraints: 'CONSTRAINTS',
      sectionTowers: 'TOWERS',
      sectionWinCondition: 'WIN CONDITION',
      budgetLabel: 'Gold budget',
      wavesLabel: 'Waves',
      towerAllowed: 'ALLOWED',
      towerForbidden: 'LOCKED',
      resultEyebrowWon: 'PUZZLE COMPLETE',
      resultEyebrowLost: 'PUZZLE FAILED',
      resultTitleWon: 'SOLVED!',
      resultTitleLost: 'TRY AGAIN',
      resultStatTowers: 'TOWERS',
      resultStatGold: 'GOLD SPENT',
      resultStatLives: 'LIVES LEFT',
      resultNextBtn: 'Next level ▷',
      resultReplayBtn: 'Replay',
      resultResetBtn: 'Reset attempt',
      resultBackBtn: 'Back to select',
    },
    // Sprint 69 (i18n-gaps-extract-s69) — puzzle level-select overlay
    // chrome (was hardcoded English in src/ui/puzzleSelect.ts).
    select: {
      subtitle: 'Hand-authored logic levels — deduce the one winning layout.',
      close: 'Close',
      cardTitle: 'P{index}  {name}',
      lockedCard: '🔒 LOCKED',
      solved: '✓ Solved',
      unsolved: '▷ Unsolved',
      noSolve: 'No solve yet',
      bestLine: 'Best: {towers}t · {gold}g · {power}p',
      allowed: 'Allowed: {list}',
      parPlaceholder: 'par: — / — / —',
      parRow: '{towers}t · {gold}g · {power}p',
    },
  },

  // -------- Locale switcher chip --------------------------------------------
  localeChip: {
    label: 'LANG',
    en: 'EN',
    cs: 'CS',
  },

  // -------- Resume prompt ---------------------------------------------------
  resume: {
    title: 'Resume previous run?',
    resume: 'Resume',
    newRun: 'New Run',
    hint: '[Enter] Resume   ·   [Esc] New',
    // Sprint 69 — Resume-prompt subtitle. `{mode}` / `{difficulty}` are pre-
    // localised (mode chip + difficulty chip labels) before being substituted.
    detail:
      '{mode} · {difficulty} · wave {wave} · {towers} tower(s) · {gold}g · {lives}♥',
  },

  // -------- Sprint 69: Kernel boss UI chrome --------------------------------
  // The HP-bar header, the phase tag (per the Sprint-8 boss spec), the pre-
  // spawn banner and the Power-Drain warning — extracted out of render.ts
  // literals so the boss fight reads in the player's language.
  boss: {
    kernelHeader: '⚠  THE KERNEL',
    phase: 'PHASE {phase} · {name}',
    powerDrain: '⚡ POWER DRAIN',
    kernelIncoming: '⚠  KERNEL  INCOMING  ⚠',
    phaseName: {
      boot: 'BOOT SEQUENCE',
      fork: 'FORK BOMB',
      panic: 'KERNEL PANIC',
    },
  },

  // -------- Sprint 69: Map registry display strings -------------------------
  // CONFIG.maps[*] carries `label` / `tagline` as English defaults — the
  // title-screen Map chip and any future map-picker UI consume these through
  // `t('maps.' + map.id + '.label')`.
  maps: {
    default: { label: 'Datawash', tagline: 'Open S-bend — the classic OVERVOLT layout.' },
    cramped: { label: 'Switchyard', tagline: 'Cramped lanes — every tile is contested.' },
    lines: { label: 'Long Lines', tagline: 'Long straights — lane the lance.' },
    serpentine: { label: 'Snake Run', tagline: 'Tight serpentine — Cryo painter shines.' },
  },

  // -------- Sprint 69: Ability display names --------------------------------
  // CONFIG.abilities[*].name carries English defaults — the ability-slot
  // panel + tooltips consume these through `t('abilities.' + id + '.label')`.
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names ----------------------------------
  overload: {
    tierName: {
      nominal: 'Nominal',
      charged: 'Charged',
      surging: 'Surging',
      critical: 'Critical',
      meltdown: 'Meltdown',
    },
  },

  // -------- Sprint 69: Endless modifier labels ------------------------------
  endlessMod: {
    armoredSurge: { label: 'Armoured Surge' },
    fastPack: { label: 'Fast Pack' },
    scatteredSpread: { label: 'Scattered Spread' },
    streakRunners: { label: 'Streak Runners' },
    daemonRush: { label: 'Daemon Rush' },
  },

  // -------- Sprint 69: Enemy display names (Help roster) --------------------
  enemies: {
    bit: { label: 'Bit' },
    packet: { label: 'Packet' },
    daemon: { label: 'Daemon' },
    fork: { label: 'Fork' },
    cipher: { label: 'Cipher' },
    patch: { label: 'Patch' },
    buffer: { label: 'Buffer' },
    streak: { label: 'Streak' },
    kernel: { label: 'Kernel' },
  },

  // -------- Sprint 69: Help-panel enemy one-liners --------------------------
  enemyRole: {
    bit: 'Basic chaff — cheap, slow, fragile.',
    packet: 'Fast and weak; it rushes the Core.',
    daemon: 'Heavy HP sponge; hits hard on a leak.',
    fork: 'Splits into two Bits when destroyed.',
    cipher: 'Armored — shred it with Mortar Corrosion.',
    patch: 'Heals nearby Glitches; focus it down first.',
    buffer: 'Regenerating shield; break it with burst damage.',
    streak: 'Sprints when alone; resists slow. Cover the path — Sniper or Mortar.',
    kernel: 'Phase boss — drains your power. Burst it before the next phase.',
  },

  // -------- Sprint 69: Help-panel ability one-liners ------------------------
  abilityRole: {
    overcharge:
      'Burst the grid: every tower fires faster for a short window. Plan it around a heavy wave.',
    airstrike: 'Drop a one-shot blast on a target tile. Panic-clear a cluster or chip a big enemy.',
    empBurst: 'Field-wide stun — every enemy freezes briefly. Buy time when the lane is overrun.',
  },

  // -------- Sprint 69: Tower / ability tooltip chrome -----------------------
  tooltip: {
    levelOf: 'LV {level}/{max}',
    credits: '{cost} credits',
    next: 'NEXT  →  LV {level}   {cost} credits',
    cooldown: 'COOLDOWN  {sec}s',
    stat: {
      supply: 'SUPPLY  +{n}w to the grid',
      dmgRngCd: 'DMG {damage}    RNG {range}    CD {cd}s',
      pwrChain: 'PWR {power}w    CHAIN {chain}',
      pwrAoe: 'PWR {power}w    AOE {targets} targets',
      slow: 'SLOW ×{factor}  for  {sec}s',
      brittle: 'BRITTLE +{pct}% dmg from other towers',
      pwrBlast: 'PWR {power}w    BLAST {tiles} tiles',
      pwrSingle: 'PWR {power}w    1 target',
    },
  },

  // -------- Sprint 69: Help-panel control-row labels ------------------------
  controls: {
    arm: 'Arm Pulse / Arc / Pylon / Cryo / Mortar / Sniper',
    mouse: 'Place on a tile · click a tower to select',
    space: 'Start wave · pause the wave',
    fast: 'Fast-forward (1× ⇄ 2×)',
    upgradeSell: 'Upgrade / sell the selected tower',
    target: 'Cycle the selected tower targeting mode',
    profile: 'Open Profile (unlock tree · scoreboard)',
    utility: 'Cancel · mute · restart the run',
  },

  // -------- Sprint 69: Unlock-tree node display names -----------------------
  unlocks: {
    towerMortar: { name: 'Splash Mortar' },
    towerSniper: { name: 'Sniper Rail' },
    abilityAirstrike: { name: 'Airstrike' },
    abilityEmp: { name: 'EMP Burst' },
    modeEndless: { name: 'Endless Mode' },
    modeMaze: { name: 'Maze Mode' },
    difficultyHard: { name: 'Hard Difficulty' },
    overloadCritical: { name: 'Overload: Critical' },
    overloadMeltdown: { name: 'Overload: Meltdown' },
    themeAmber: { name: 'Amber Terminal' },
    themePhosphor: { name: 'Phosphor Green' },
  },

  // -------- Sprint 73: Interactive tutorial (12-step opt-in tour) ----------
  // English source-of-truth for the tour copy locked in `docs/TUTORIAL_DESIGN.md`
  // §6.2. Every visible string in `src/ui/tutorial.ts` flows through these
  // keys — title-screen button, per-step title + body, overlay chrome.
  tutorial: {
    button: {
      start: 'START TUTORIAL',
      startSub: 'First time? Take the tour →',
      replay: 'REPLAY TOUR',
      replaySub: 'Refresh the basics',
    },
    step: {
      welcome: {
        title: 'Welcome to OVERVOLT',
        body: 'You are the defence AI of a failing reactor. Corrupted programs march down the circuit toward your Core — destroy them before they break through.',
      },
      path: {
        title: 'The path',
        body: 'Enemies always follow this S-bend, from the spawn tile on the left to the Core on the right. You decide where to plant turrets along it.',
      },
      firstPlace: {
        title: 'Place your first turret',
        body: 'A Pulse Turret costs 50 credits and draws 4 power. Click the glowing tile to drop one next to the path.',
      },
      wave1: {
        title: 'Send the first wave',
        body: 'Press START WAVE. Your Pulse fires automatically at whatever is in range.',
      },
      towerRoles: {
        title: 'Five towers, five jobs',
        body: 'Pulse: reliable anywhere. Sniper: long straights. Mortar: indirect fire, place BACK from the path. Arc: scales with power surplus. Cryo: paints one chokepoint with slow + brittle.',
      },
      powerGrid: {
        title: 'The power grid',
        body: 'Every tower draws power. When demand outruns supply the grid browns out and every tower slows. Build Pylons to keep the lights on — adjacent Pylons buff fire rate.',
      },
      overloadDial: {
        title: 'Overload — risk vs reward',
        body: 'Crank the dial up for the next wave: tougher enemies, bigger Flux. Dial down to play it safe.',
      },
      overclock: {
        title: 'Overclock a tower',
        body: 'Toggle OVERCLOCK on a selected tower for a burst window: faster firing, heavier power draw. Bursty win, steady-state loss.',
      },
      abilities: {
        title: 'Active abilities',
        body: 'Three slots: Overcharge bursts the whole grid. Airstrike drops a lasting scorch zone. EMP Burst stuns every enemy on the field. Plan them — don’t panic-press.',
      },
      mazePreview: {
        title: 'Maze mode',
        body: 'No fixed path — towers block the tiles they sit on and enemies A*-path around them. You build the route they must walk.',
      },
      puzzlePreview: {
        title: 'Puzzle mode',
        body: 'Hand-crafted logic levels with line-of-sight walls. Fixed budget, fixed wave — find the one winning layout. A different game.',
      },
      wrapUp: {
        title: 'You’re ready',
        body: 'Runs earn FLUX — spend it in your Profile to unlock new towers, abilities, and modes. Start with the Campaign; come back for Endless, Maze, and Puzzle.',
      },
    },
    ui: {
      eyebrow: 'STEP {n} / {total}',
      next: 'NEXT →',
      back: '← BACK',
      skip: 'SKIP TOUR',
      play: 'PLAY  ▸',
      skipConfirm: 'Skip the tour? You can replay it any time from the title screen.',
    },
  },

  // -------- Sprint 62: Auth start-screen modal ------------------------------
  // Backend (Cognito + Discord/Google) ships in a later sprint — this sprint
  // only renders the frontend choice + a "coming soon" placeholder on the
  // sign-in buttons. The Play-as-guest path goes straight into the legacy
  // localStorage-only flow. See docs/AUTH_DESIGN.md §3.
  auth: {
    title: 'OVERVOLT',
    subtitle: 'Sign in to sync your progress across devices — or play as a guest, just like you do today. Your run is never lost.',
    signInDiscord: '⚡  Sign in with Discord',
    signInGoogle: 'G   Sign in with Google',
    signInEmail: '✉   Sign in with email link',
    playAsGuest: '→   Play as guest',
    comingSoon:
      'Coming soon — backend is being set up. For now, sign in becomes available when the deploy ships.',
    guestMode: 'Playing as guest — your progress saves to this device.',
    orDivider: 'or',
    signOut: 'Sign out',
    signOutBody:
      'Sign out — your cloud save stays safe. You\'ll need to sign in again to sync new runs.',
  },

  // -------- Sprint 62: Cookies banner ---------------------------------------
  // The "no tracking, no ads, no third parties" pledge in the body line is a
  // real OVERVOLT differentiator — see MARKETING_PLAN.md §7.1 and the
  // banner-style notes in docs/AUTH_DESIGN.md §5.2. Both buttons have the
  // same visual weight; "Just essentials" is a real choice, not a dark
  // pattern.
  cookies: {
    title: 'We remember you with cookies',
    body: 'OVERVOLT uses a few essential cookies so you stay signed in across sessions. No tracking, no ads, no third parties (besides your chosen sign-in provider).',
    accept: 'Accept',
    essentials: 'Just essentials',
  },
};
